/**
 * questLogFormat — PURE formatting helpers for the Quest Log + Town quest dialogs.
 *
 * No Phaser, no IO: turns the frozen quest data shapes (shared/quest.ts) into the
 * warm, pressure-free strings the scenes render, so the wording is unit-testable in
 * a plain node environment (the repo's pure-helper convention — cf. loreNav/menuNav).
 *
 * The wording obeys the no-pressure contract (CLAUDE.md + D17): every line reads as a
 * gentle note of where you are, NEVER a nag, a deadline, an "overdue", or a red X. A
 * not-yet-done objective is phrased as an inviting "to do", a done one as a warm tick.
 */
import type { ObjectiveDef, ObjectiveProgress, QuestDef, QuestReward } from '@/shared';

/** Optional name resolvers the scene wires to the live dataset; all pure + optional. */
export interface ObjectiveLookup {
  speciesName?(id: string): string | undefined;
  moveName?(id: string): string | undefined;
  trainerName?(id: string): string | undefined;
}

/**
 * Turn an id-ish slug ('still-lake', 'npc-leader-meadow', 'deep-forest-badge') into a
 * warm Title-Cased label, dropping structural words. Used for givers/biomes/gyms when
 * no authored display name is available, so the log always reads as prose.
 */
export function humanizeId(id: string, drop: readonly string[] = DEFAULT_DROP): string {
  const words = id.split(/[-_\s]+/).filter((w) => w.length > 0 && !drop.includes(w.toLowerCase()));
  if (words.length === 0) return titleCase(id.replace(/[-_]+/g, ' '));
  return words.map(capitalize).join(' ');
}

const DEFAULT_DROP = ['npc', 'leader', 'badge', 'gym'] as const;

function capitalize(w: string): string {
  return w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1);
}
function titleCase(s: string): string {
  return s.split(' ').map(capitalize).join(' ');
}

/** A soft "N of M" phrase — never framed as a shortfall. */
function tally(current: number, target: number): string {
  const cur = Math.max(0, Math.min(current, target));
  return `${cur} of ${target}`;
}

/**
 * A single warm progress line for one objective (Quest Log Active rows). `prog` is the
 * matching ObjectiveProgress (by positional index) or undefined when not yet tracked.
 * Done objectives read as a gentle "✓ …"; pending ones as an inviting to-do.
 */
export function objectiveLine(
  obj: ObjectiveDef,
  prog: ObjectiveProgress | undefined,
  lookup: ObjectiveLookup = {},
): string {
  const done = prog?.done ?? false;
  const count = prog?.count ?? 0;
  const tick = (s: string): string => (done ? `✓ ${s}` : s);

  switch (obj.kind) {
    case 'catch-species': {
      const name = lookup.speciesName?.(obj.speciesId) ?? humanizeId(obj.speciesId, []);
      return done ? `✓ Caught ${name}` : `Find and catch ${name}`;
    }
    case 'catch-type':
      return tick(`Caught ${tally(count, obj.count)} ${obj.monType}-type Pokémon`);
    case 'catch-count':
      return tick(`Caught ${tally(count, obj.count)} wild Pokémon`);
    case 'win-friendly-battles':
      return tick(`Enjoyed ${tally(count, obj.count)} friendly battles`);
    case 'defeat-gym-leader': {
      const gym = humanizeId(obj.gymId);
      return done ? `✓ Cleared the ${gym} Gym` : `Take on the ${gym} Gym when you like`;
    }
    case 'reach-level': {
      const who = obj.speciesId
        ? (lookup.speciesName?.(obj.speciesId) ?? humanizeId(obj.speciesId, []))
        : 'a partner';
      return done ? `✓ Grew ${who} to Lv. ${obj.level}` : `Grow ${who} to Lv. ${obj.level}`;
    }
    case 'visit-biome': {
      const place = humanizeId(obj.biomeId);
      return done ? `✓ Visited ${place}` : `Wander out to ${place}`;
    }
    case 'evolve': {
      if (obj.toSpeciesId) {
        const name = lookup.speciesName?.(obj.toSpeciesId) ?? humanizeId(obj.toSpeciesId, []);
        return done ? `✓ Evolved into ${name}` : `Evolve a friend into ${name}`;
      }
      return tick(`Shared ${tally(count, obj.count)} evolutions`);
    }
    case 'trade': {
      if (obj.withTrainerId) {
        const who = lookup.trainerName?.(obj.withTrainerId) ?? humanizeId(obj.withTrainerId);
        return done ? `✓ Traded with ${who}` : `Trade with ${who}`;
      }
      return tick(`Made ${tally(count, obj.count)} trades`);
    }
    case 'show-off': {
      const name = obj.speciesId
        ? (lookup.speciesName?.(obj.speciesId) ?? humanizeId(obj.speciesId, []))
        : undefined;
      const what = name ? `${name}` : 'a partner';
      return tick(`Showed off ${what} ${tally(count, obj.count)} times`);
    }
    case 'cast-move': {
      const move = obj.moveId
        ? (lookup.moveName?.(obj.moveId) ?? humanizeId(obj.moveId, []))
        : null;
      const what = move ?? (obj.monType ? `a ${obj.monType} move` : 'a move');
      return tick(`Cast ${what} ${tally(count, obj.count)} times`);
    }
    case 'talk-to': {
      const who = lookup.trainerName?.(obj.trainerId) ?? humanizeId(obj.trainerId);
      return done ? `✓ Said hi to ${who}` : `Say hi to ${who}`;
    }
    default:
      // Exhaustive in practice; a safe warm fallback keeps the log from ever going blank.
      return 'Keep exploring — this one unfolds as you play';
  }
}

/**
 * All soft progress lines for a quest's CURRENTLY-ACTIVE stage. Returns a single warm
 * "ready to share" note when every stage is complete (stageIndex past the last stage),
 * so a finished-but-not-turned-in quest reads as a happy invitation, never a chore.
 */
export function activeStageLines(
  def: QuestDef,
  stageIndex: number,
  objectives: readonly ObjectiveProgress[],
  giverName: string,
  lookup: ObjectiveLookup = {},
): string[] {
  if (stageIndex >= def.stages.length) {
    return [`All done — go share the good news with ${giverName}!`];
  }
  const stage = def.stages[stageIndex];
  if (!stage) return ['Keep at your own pace — no rush at all.'];
  return stage.objectives.map((obj, i) =>
    objectiveLine(
      obj,
      objectives.find((p) => p.index === i),
      lookup,
    ),
  );
}

/** A warm, spoiler-free one-line description of a quest reward (turn-in flavor). */
export function rewardLine(reward: QuestReward, lookup: ObjectiveLookup = {}): string {
  switch (reward.kind) {
    case 'gift-mon': {
      const name =
        reward.nickname ??
        lookup.speciesName?.(reward.speciesId) ??
        humanizeId(reward.speciesId, []);
      return `${name} would love to join you — welcome to the team!`;
    }
    case 'codex-unlock':
      return 'A new page quietly opened in your Lore Codex.';
    case 'badge':
      return `You earned the ${humanizeId(reward.badgeId)} Badge!`;
    case 'rare-candies':
      return `A little burst of growth to share among your team.`;
    case 'evolution-stone':
      return `A ${humanizeId(reward.itemId)} — waiting at the Training Grounds whenever you want it.`;
    case 'town-prop':
      return 'The town feels a touch warmer for it.';
    case 'follower-cosmetic':
      return 'A new companion touch is yours to enjoy.';
    default:
      return 'A little something, with thanks.';
  }
}

/** Whether a started quest is complete (all stages cleared) but not yet turned in. */
export function isReadyToTurnIn(def: QuestDef, stageIndex: number, turnedIn: boolean): boolean {
  return !turnedIn && stageIndex >= def.stages.length;
}
